/**
 * Admin-app ErrorBoundary (W0.8).
 *
 * Mirrors the customer-app ErrorBoundary. Catches render errors anywhere
 * below it and replaces the crashed subtree with a brand-styled
 * "Something went wrong" card. Uses `@ferro/ui` Card + the `ferro-*`
 * CSS classes so the fallback respects the active brand.
 *
 * For per-page fallbacks see PageErrorBoundary.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Card } from '@ferro/ui';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[admin] ErrorBoundary caught:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private readonly handleReload = (): void => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  private readonly handleGoHome = (): void => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <main
        className="ferro-admin-page ferro-error-boundary"
        role="alert"
        aria-live="assertive"
      >
        <Card title="Something went wrong">
          <p className="ferro-muted">
            We hit an unexpected issue. Please try again.
          </p>
          <div className="ferro-error-boundary__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={this.handleReload}
            >
              Reload
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={this.handleGoHome}
            >
              Go to dashboard
            </button>
          </div>
        </Card>
      </main>
    );
  }
}
